import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * `/booking/success?session_id=cs_xxx` — landing page after Stripe Checkout.
 *
 * Per playbook, we poll `/api/payments/status/{session_id}` until it flips
 * to `paid` (max ~30s). The status endpoint has a webhook fallback that
 * asks Stripe directly, so polling resolves on the first tick in practice.
 */
export default function BookingSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'polling', tx: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', tx: null });
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data?.payment_status === 'paid') {
          setState({ status: 'paid', tx: res.data });
          return;
        }
        if (res.data?.payment_status === 'expired' || res.data?.payment_status === 'failed') {
          setState({ status: 'failed', tx: res.data });
          return;
        }
      } catch (err) {
        if (attempts > 5) {
          setState({ status: 'error', tx: null });
          return;
        }
      }
      attempts += 1;
      if (attempts > 15) {
        // ~30 seconds. Shouldn't happen in practice — webhook + inline
        // fallback usually resolves on the first tick.
        setState({ status: 'timeout', tx: null });
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const renderBody = () => {
    if (!sessionId) {
      return (
        <div className="text-center space-y-3" data-testid="booking-no-session">
          <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
          <h2 className="font-bold text-lg">No checkout session provided</h2>
          <p className="text-sm text-muted-foreground">If you just paid and landed here unexpectedly, contact <a className="underline" href="mailto:support@eventism.com">support@eventism.com</a>.</p>
        </div>
      );
    }
    if (state.status === 'polling') {
      return (
        <div className="text-center space-y-3" data-testid="booking-polling">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-500 mx-auto" />
          <h2 className="font-bold text-lg">Confirming your payment…</h2>
          <p className="text-sm text-muted-foreground">This usually takes 1–2 seconds. Hang tight.</p>
        </div>
      );
    }
    if (state.status === 'paid') {
      const kindLabel = state.tx?.kind === 'reservation_deposit' ? 'Reservation' : 'Booking';
      return (
        <div className="text-center space-y-3" data-testid="booking-paid">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
          <h2 className="font-bold text-2xl">You're confirmed.</h2>
          <p className="text-sm text-muted-foreground">
            {kindLabel} #{state.tx?.booking_ref?.slice(0, 14)}… is locked in. A confirmation email is on its way.
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <Link to="/"><Button variant="outline" size="sm">Browse more</Button></Link>
          </div>
        </div>
      );
    }
    if (state.status === 'failed') {
      return (
        <div className="text-center space-y-3" data-testid="booking-failed">
          <AlertCircle className="h-10 w-10 text-rose-500 mx-auto" />
          <h2 className="font-bold text-lg">Payment didn't go through</h2>
          <p className="text-sm text-muted-foreground">Your card wasn't charged. Please try booking again.</p>
        </div>
      );
    }
    if (state.status === 'timeout') {
      return (
        <div className="text-center space-y-3" data-testid="booking-timeout">
          <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
          <h2 className="font-bold text-lg">We're still confirming…</h2>
          <p className="text-sm text-muted-foreground">Your payment is being processed. Check your email shortly, or reload this page in a minute.</p>
        </div>
      );
    }
    return (
      <div className="text-center space-y-3" data-testid="booking-error">
        <AlertCircle className="h-10 w-10 text-rose-500 mx-auto" />
        <h2 className="font-bold text-lg">Something went wrong</h2>
        <p className="text-sm text-muted-foreground">Email <a className="underline" href="mailto:support@eventism.com">support@eventism.com</a> with your session id and we'll sort it out.</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-16 px-4" data-testid="booking-success-page">
      <div className="max-w-md mx-auto">
        <Card>
          <CardContent className="p-6">{renderBody()}</CardContent>
        </Card>
      </div>
    </div>
  );
}
